/**
 * useTokenPrice — Prompt 38 Part 2.2.
 *
 * Auto-refreshing CoinGecko fetch with two important behaviors:
 *   1. Pauses polling when the browser tab is hidden (saves API calls).
 *   2. Default 10-second interval respects the backend's 60-second cache —
 *      we won't hit upstream more than once per cache cycle.
 *
 * Returns the same `data`, `loading`, `error`, `refetch` shape regardless
 * of whether the data came from CoinGecko or the DexScreener fallback —
 * the caller renders a source banner based on `data.source`.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { getCoinDetail } from '../services/api';

const DEFAULT_INTERVAL_MS = 10000; // 10s — backend caches for 60s

export default function useTokenPrice(coinId, refreshIntervalMs = DEFAULT_INTERVAL_MS) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);
  const cancelledRef = useRef(false);

  const fetchPrice = useCallback(async () => {
    if (!coinId) { setLoading(false); return; }
    try {
      const r = await getCoinDetail(coinId);
      if (cancelledRef.current) return;
      // Backend wraps the payload in `{ success, data, fetchedAt, fromCache }`.
      // Keep both the data and the freshness metadata accessible to the page.
      const inner = r?.data || r;
      // If backend returned the fetchedAt at the top level, fold it onto data.
      if (r?.fetchedAt && inner && typeof inner === 'object' && !inner.fetchedAt) {
        inner.fetchedAt = r.fetchedAt;
      }
      if (r?.fromCache !== undefined && inner && typeof inner === 'object' && inner.fromCache === undefined) {
        inner.fromCache = r.fromCache;
      }
      setData(inner);
      setError(null);
    } catch (e) {
      if (cancelledRef.current) return;
      setError(e);
    } finally {
      if (!cancelledRef.current) setLoading(false);
    }
  }, [coinId]);

  useEffect(() => {
    cancelledRef.current = false;
    setLoading(true);
    setError(null);

    fetchPrice();

    function startPolling() {
      clearInterval(intervalRef.current);
      intervalRef.current = setInterval(fetchPrice, refreshIntervalMs);
    }
    function stopPolling() {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (!document.hidden) startPolling();

    function handleVisibility() {
      if (document.hidden) {
        stopPolling();
      } else {
        // Returning to the tab — refresh immediately, then resume polling.
        fetchPrice();
        startPolling();
      }
    }
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      cancelledRef.current = true;
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [fetchPrice, refreshIntervalMs]);

  return { data, loading, error, refetch: fetchPrice };
}

export { useTokenPrice };
